import { ServerError, ServerErrorMessages } from '../../shared/utils/server-error-utils';
import { mapAxiosResponse } from '../../shared/utils/api-utils';
import AuthUtils from '../../shared/utils/auth-utils';
import { UserInfoResponse } from '../../shared/models/user';
import { Organization } from '../../shared/models/organization';
import ErcApiClient from '../../shared/services/erc-api-client';
import EnrouteApiClient from '../../shared/services/enroute-api-client';
import { RestoreApiOptions } from '../../shared/models/restore';

export interface LoginApiOptions {
  username?: string;
  password?: string;
  token?: string;
}

export interface LoginSuccessResponse {
  core_login: CoreLoginData;
  enroute_login: EnrouteLoginData;
  self: UserInfoResponse;
  org: Organization;
}

export interface CoreLoginData {
  access_token: string;
  account_created: number;
  config: string;
  expires_in: number;
  token_type: string;
  type: string;
}

export interface EnrouteLoginData {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface LoginStatus {
  authed: boolean,
  sessionToken: string,
  sessionTokenExpiration: Date,
  errorText: string,
}

export default class LoginService {
  public static async login(options: LoginApiOptions): Promise<LoginSuccessResponse | Error> {
    return ErcApiClient.post('/login', options)
      .then((resp) => mapAxiosResponse<LoginSuccessResponse>(resp))
      .then((response: LoginSuccessResponse) => {
        const { access_token } = response.enroute_login;
        const expires_in = response?.core_login?.expires_in || 604800;
        const accessTokenExpiration = String(Date.now() + expires_in * 1000);

        AuthUtils.setAccessToken(access_token);
        AuthUtils.setAccessTokenExp(accessTokenExpiration);
        AuthUtils.setAuthToken(response.core_login.access_token);
        AuthUtils.removeSessionHasExpired();

        return response;
      })
      .catch((error) => {
        let errorMessage = ServerErrorMessages[error.message];
        if (!errorMessage) {
          errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
        }
        throw new Error(errorMessage);
      });
  }

  public static async logout(): Promise<any | Error> {
    const response = await EnrouteApiClient.post('/account/logout');

    AuthUtils.removeAccessToken();
    AuthUtils.removeAccessTokenExp();
    AuthUtils.removeAuthToken();

    return response;
  }

  public static async restore(apiOptions: RestoreApiOptions): Promise<any | Error> {
    const response = await EnrouteApiClient.post('/account/restore', {
      agent: {
        username: apiOptions.username
      },
      captcha: {
        response: apiOptions.captcha
      },
      flow: apiOptions.flow
    });
    return mapAxiosResponse(response);
  }

  public static hasExpired(): boolean {
    return !AuthUtils.isAuth();
  }

  public static async markExpired() {
    AuthUtils.setSessionHasExpired();
  }
}
